import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Lock, 
  Eye, 
  Zap, 
  CheckCircle2, 
  ArrowLeft,
  Server,
  FileText,
  Users,
  Clock,
  AlertTriangle,
  Database,
  Key,
  Globe
} from 'lucide-react';

const SecurityPrivacy = () => {
  const securityFeatures = [
    {
      icon: Lock,
      title: 'AES-256 Encryption',
      description: 'All sensitive data is encrypted using AES-256, the same encryption standard used by banks and government agencies.',
      details: [
        'Data encrypted at rest in our database',
        'TLS 1.3 encryption for all data in transit',
        'Secure key management with regular rotation',
        'Field-level encryption for sensitive business information'
      ],
      color: 'green'
    },
    {
      icon: Eye,
      title: 'Complete Audit Trail',
      description: 'Every action in the system is logged and traceable, providing full accountability and transparency.',
      details: [
        'All login attempts tracked with IP and timestamp',
        'Data access and modifications logged',
        'Export activities monitored and recorded',
        '365-day audit log retention'
      ],
      color: 'blue'
    },
    {
      icon: Zap,
      title: '24/7 Threat Monitoring',
      description: 'Automated security systems continuously monitor for and respond to potential threats.',
      details: [
        'Real-time suspicious activity detection',
        'Automatic IP blocking after failed attempts',
        'Rate limiting on sensitive endpoints',
        'Instant email alerts for security events'
      ],
      color: 'purple'
    },
    {
      icon: Shield,
      title: 'Access Control',
      description: 'Strict role-based access ensures users only see what they need to see.',
      details: [
        'Role-based permissions (Client, Consultant, Admin)',
        'Session management with secure tokens',
        'Automatic session expiration',
        'Multi-device session tracking'
      ],
      color: 'amber'
    }
  ];

  const compliancePractices = [
    {
      icon: FileText,
      title: 'SOC 2 Aligned Practices',
      description: 'Our security controls align with SOC 2 Type II principles for service organizations.'
    },
    {
      icon: Globe,
      title: 'GDPR Compliance',
      description: 'Data handling practices designed to meet GDPR requirements for data protection and privacy.'
    },
    {
      icon: Server,
      title: 'Secure Infrastructure',
      description: 'Hosted on enterprise-grade cloud infrastructure with redundancy and disaster recovery.'
    },
    {
      icon: Clock,
      title: '99.9% Uptime SLA',
      description: 'Reliable service availability with continuous monitoring and rapid incident response.'
    }
  ];

  const faqs = [
    {
      question: 'How is my business data protected?',
      answer: 'Your data is protected using AES-256 encryption, the same standard used by financial institutions. All data is encrypted both at rest (stored) and in transit (when being sent). We also implement field-level encryption for especially sensitive information like financial data and strategic plans.'
    },
    {
      question: 'Who can access my information?',
      answer: 'Only your assigned consultant(s) and authorized system administrators can access your information. All access is logged and auditable. We implement strict role-based access controls, meaning each user only sees what they need to see based on their role.'
    },
    {
      question: 'How do you handle data breaches?',
      answer: 'We have comprehensive incident response procedures in place. In the unlikely event of a breach, affected users would be notified within 72 hours as required by GDPR. We conduct regular security assessments to proactively identify and address vulnerabilities.'
    },
    {
      question: 'Can I request my data be deleted?',
      answer: 'Yes, you have the right to request deletion of your personal data at any time. Contact your consultant or our support team, and we will process your request in accordance with our data retention policies and applicable regulations.'
    },
    {
      question: 'Do you share my data with third parties?',
      answer: 'We never sell or share your business data with third parties for marketing purposes. Data is only shared with essential service providers (such as payment processors) as needed to deliver our services, and these providers are bound by strict confidentiality agreements.'
    },
    {
      question: 'How long is my data retained?',
      answer: 'Active client data is retained for the duration of your engagement plus a reasonable period afterward for legal and business continuity purposes. Audit logs are retained for 365 days. You can request data deletion at any time, subject to legal retention requirements.'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
      blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
      purple: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
      amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' }
    };
    return colors[color] || colors.green;
  };

  return (
    <div className="min-h-screen bg-neutral-950">
      {/* Header */}
      <header className="border-b border-neutral-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/mybiz-logo.png" 
                alt="My Biz Consulting" 
                className="h-8 w-auto brightness-0 invert"
              />
              <div>
                <p className="text-sm font-semibold text-white leading-tight">My Biz Consulting</p>
                <p className="text-[10px] text-[#39FF14] font-medium uppercase tracking-widest">Business Solutions Center</p>
              </div>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 rounded-full border border-green-500/20 mb-6">
            <Shield className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium text-green-400">Enterprise-Grade Security</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Security & Privacy
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            Your business data deserves the highest level of protection. Learn how we safeguard your confidential information with industry-leading security measures.
          </p>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-12 bg-neutral-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold text-white mb-8 text-center">
            How We Protect Your Data
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              const colors = getColorClasses(feature.color);
              return (
                <div 
                  key={index}
                  className={`bg-neutral-900 rounded-xl p-6 border ${colors.border}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                      <p className="text-neutral-400 text-sm mb-4">{feature.description}</p>
                      <ul className="space-y-2">
                        {feature.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-neutral-500">
                            <CheckCircle2 className={`w-4 h-4 ${colors.text} flex-shrink-0 mt-0.5`} />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance & Standards */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold text-white mb-8 text-center">
            Compliance & Standards
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {compliancePractices.map((practice, index) => {
              const Icon = practice.icon;
              return (
                <div 
                  key={index}
                  className="bg-neutral-900/50 rounded-xl p-5 border border-neutral-800 text-center"
                >
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="font-medium text-white mb-2 text-sm">{practice.title}</h3>
                  <p className="text-xs text-neutral-500">{practice.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data Handling */}
      <section className="py-12 bg-neutral-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold text-white mb-8 text-center">
            Your Rights & Our Commitment
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                <Database className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Data Portability</h3>
              <p className="text-sm text-neutral-400">
                You can request an export of all your data at any time. We provide your information in a standard, machine-readable format.
              </p>
            </div>
            <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800">
              <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center mb-4">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Right to Delete</h3>
              <p className="text-sm text-neutral-400">
                Request deletion of your personal data at any time. We will remove your information within 30 days, subject to legal retention requirements.
              </p>
            </div>
            <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">No Data Selling</h3>
              <p className="text-sm text-neutral-400">
                We never sell your data. Your business strategies and information are confidential and used solely to serve you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold text-white mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group bg-neutral-900/50 rounded-xl border border-neutral-800 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  <span className="text-neutral-500 group-open:rotate-180 transition-transform">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-5 pb-5 text-sm text-neutral-400 border-t border-neutral-800 pt-4">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 bg-neutral-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 rounded-full mb-4">
            <Key className="w-4 h-4 text-neutral-400" />
            <span className="text-sm text-neutral-400">Security Questions?</span>
          </div>
          <h2 className="text-xl font-semibold text-white mb-3">
            Have Security or Privacy Concerns?
          </h2>
          <p className="text-neutral-400 mb-6">
            We take security seriously. If you have questions or concerns about how we handle your data, please reach out.
          </p>
          <a 
            href="mailto:security@mybizconsulting.net"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors font-medium"
          >
            Contact Security Team
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-neutral-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/mybiz-logo.png" 
                alt="My Biz Consulting" 
                className="h-6 w-auto brightness-0 invert"
              />
              <span className="text-neutral-500 text-sm">Business Solutions Center</span>
            </div>
            <p className="text-neutral-600 text-sm">
              © {new Date().getFullYear()} My Biz Consulting. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecurityPrivacy;
